import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { getLegalBases, getDataCategories } from 'services/mapping-service';
import { getProcessById } from 'services/processes-service';
import { FILTER } from '../filter-types';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const GREEN = 'var(--vo-success)';
const DIVIDER = 'var(--vo-cloud)';

const TREATMENT_COLORS = {
  'Armazenar':    { headerBg: 'var(--vo-treatment-store-grad)', headerColor: 'var(--vo-treatment-store)', bodyBg: 'var(--vo-treatment-store-bg)', divider: 'color-mix(in srgb, var(--vo-treatment-store) 20%, transparent)' },
  'Compartilhar': { headerBg: 'var(--vo-treatment-share-grad)', headerColor: 'var(--vo-treatment-share)', bodyBg: 'var(--vo-treatment-share-bg)', divider: 'color-mix(in srgb, var(--vo-treatment-share) 20%, transparent)' },
  'Coletar':      { headerBg: 'var(--vo-treatment-collect-grad)', headerColor: 'var(--vo-treatment-collect)', bodyBg: 'var(--vo-treatment-collect-bg)', divider: 'color-mix(in srgb, var(--vo-treatment-collect) 20%, transparent)' },
  'Excluir':      { headerBg: 'var(--vo-treatment-delete-grad)', headerColor: 'var(--vo-treatment-delete)', bodyBg: 'var(--vo-treatment-delete-bg)', divider: 'color-mix(in srgb, var(--vo-treatment-delete) 20%, transparent)' },
};
const DEFAULT_TREATMENT = { headerBg: 'linear-gradient(90deg,var(--vo-cloud),var(--vo-line))', headerColor: 'var(--vo-ink-2)', bodyBg: 'var(--vo-mist)', divider: 'color-mix(in srgb, var(--vo-ink) 10%, transparent)' };

/* ── Page-view header card ── */
const HeaderCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const ProcessLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;
const ReportName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 8px;
`;
const ProcessMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const ScopeBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const MetaDateText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const StatPill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const ResponsibleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--vo-mist);
`;
const ResponsibleText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;
const DotSep = styled.span`
  color: var(--vo-line);
`;
const AreaLbl = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

/* ── Page-view description card ── */
const DescCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const DescCardLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 8px;
`;
const DescCardText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
  line-height: 1.65;
`;

/* ── Page-view data section card ── */
const PageDataCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const PageDataHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${DIVIDER};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const PageDataTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const PageDataBlock = styled.div`
  border-top: 1px solid ${DIVIDER};
`;

/* ── Process accordion ── */
const ProcessWrap = styled.div`
  border: 1px solid var(--vo-capri-100);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
`;
const ProcessHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  background: var(--vo-capri-50);
  cursor: pointer;
  &:hover { background: var(--vo-capri-50); }
`;
const ProcessLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;
const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
`;
const ProcessName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 1px 8px;
  border-radius: 10px;
`;
const ResponsibleTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const Chevron = styled.svg`
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

/* ── Accordion description ── */
const DescSection = styled.div`
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid ${DIVIDER};
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .07em;
  margin: 0 0 6px;
`;
const DescText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
  line-height: 1.65;
`;

/* ── Shared: data mapping ── */
const DataBlock = styled.div`
  border-top: 1px solid var(--vo-capri-100);
`;
const DataHeader = styled.div`
  padding: 14px 16px 12px;
  background: linear-gradient(135deg, var(--vo-capri-100) 0%, var(--vo-capri-200) 100%);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;
const DataName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 6px;
`;
const DataCategoryBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-celeste);
  background: var(--vo-info-15);
  padding: 2px 10px;
  border-radius: 20px;
`;
const DataTreatmentBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${GREEN};
  background: color-mix(in srgb, var(--vo-success) 12%, transparent);
  padding: 3px 12px;
  border-radius: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: var(--vo-treatment-collect-bg);
  padding: 12px 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const GridCell = styled.div`
  padding: 6px 10px;
`;
const CellLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .07em;
  margin: 0 0 4px;
`;
const CellValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${INK};
  margin: 0;
  font-weight: 500;
`;

/* ── Shared: treatment ── */
const TreatmentWrap = styled.div`
  border-top: 1px solid ${DIVIDER};
`;
const TreatmentHeader = styled.div`
  padding: 10px 16px;
  background: ${p => p.$bg ?? 'var(--vo-success-10)'};
  display: flex;
  align-items: center;
  gap: 7px;
`;
const TreatmentLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$color ?? GREEN};
  margin: 0;
`;
const TreatmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 12px 16px;
  background: ${p => p.$bg ?? 'var(--vo-treatment-store-bg)'};
`;
const TGridCell = styled.div`
  padding: 6px 10px;
  ${p => p.$span && `grid-column: ${p.$span};`}
  ${p => p.$borderTop && `border-top: 1px solid ${p.$dividerColor ?? 'var(--vo-success-20)'}; margin-top: 4px;`}
`;
const TGridLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .07em;
  margin: 0 0 4px;
`;
const TGridValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${INK};
  margin: 0;
`;
const TGridValueMuted = styled(TGridValue)`
  color: ${MUTED};
`;

const EmptyTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  text-align: center;
  padding: 20px;
  margin: 0;
`;

const formatDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('pt-BR');
};

const TreatmentBlock = ({ dp, t, legalBaseByKey }) => {
  const tc = TREATMENT_COLORS[dp.processing] ?? DEFAULT_TREATMENT;
  return (
    <TreatmentWrap>
      <TreatmentHeader $bg={tc.headerBg}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={tc.headerColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <TreatmentLabel $color={tc.headerColor}>{dp.processing}</TreatmentLabel>
      </TreatmentHeader>
      <TreatmentGrid $bg={tc.bodyBg}>
        <TGridCell>
          <TGridLabel>{t('mapping:ropa.process.finality')}</TGridLabel>
          <TGridValue>{dp.finality || '—'}</TGridValue>
        </TGridCell>
        <TGridCell>
          <TGridLabel>{t('mapping:ropa.process.position')}</TGridLabel>
          <TGridValue>{dp.position || '—'}</TGridValue>
        </TGridCell>
        <TGridCell>
          <TGridLabel>{t('mapping:ropa.process.tool')}</TGridLabel>
          <TGridValue>{dp.tool || '—'}</TGridValue>
        </TGridCell>
        <TGridCell>
          <TGridLabel>{t('mapping:ropa.process.legalBase')}</TGridLabel>
          <TGridValue>{legalBaseByKey.get(dp.legalBase) ?? dp.legalBase ?? '—'}</TGridValue>
        </TGridCell>
        <TGridCell $span="2/-1">
          <TGridLabel>{t('mapping:ropa.process.complementLegalBasis')}</TGridLabel>
          <TGridValueMuted>{dp.complementLegalBasis || '—'}</TGridValueMuted>
        </TGridCell>
        {dp.shareType && (
          <>
            <TGridCell>
              <TGridLabel>{t('mapping:ropa.process.shareType')}</TGridLabel>
              <TGridValue>{dp.shareType}</TGridValue>
            </TGridCell>
            <TGridCell>
              <TGridLabel>{t('mapping:ropa.process.destiny')}</TGridLabel>
              <TGridValue>{dp.destiny || '—'}</TGridValue>
            </TGridCell>
          </>
        )}
        <TGridCell $span="1/-1" $borderTop $dividerColor={tc.divider}>
          <TGridLabel>{t('mapping:dataMapping.fields.observation')}</TGridLabel>
          <TGridValueMuted>{dp.observation || '—'}</TGridValueMuted>
        </TGridCell>
      </TreatmentGrid>
    </TreatmentWrap>
  );
};

export const ReportsRopaProcess = ({
  process,
  isPageView = false,
  ropaName,
  ropaScope,
  ropaDate,
  ropaArea,
  filter,
  data,
  category,
  legalBase,
  processing,
  position,
}) => {
  const { t, i18n } = useTranslation(['dashboard', 'mapping']);
  const [open, setOpen] = useState(false);
  const selectedTenant = useSelector(useAuthTenant);

  const { data: legalBasesList } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getLegalBases(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: dataCategoriesList } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getDataCategories(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: processDetails } = useQuery(
    ['getProcessForArea', process?._id],
    async () => {
      const result = await getProcessById(process._id);
      return result?.data ?? null;
    },
    { enabled: isPageView && !!process?._id && !ropaArea },
  );

  const effectiveArea = ropaArea || processDetails?.areas?.[0]?.name;

  const legalBaseByKey = new Map((legalBasesList ?? []).map(lb => [lb.key, lb.name]));
  const categoryByKey = new Map((dataCategoriesList ?? []).map(c => [c.key, c.name]));

  const totalData = process.dataMapping?.length || 0;
  const totalTreatments = process.dataMapping?.reduce((acc, dm) =>
    acc + (dm.dataProcessing?.length || 0), 0) || 0;

  const filteredData = process.dataMapping?.filter(dataMap => {
    if (filter === FILTER.DATA && !!data) return dataMap.name === data;
    if (filter === FILTER.CATEGORY && !!category) return dataMap.category === category;
    if (filter === FILTER.PROCESSING && !!processing)
      return dataMap.dataProcessing.some(dp => dp.processing === processing);
    if (filter === FILTER.LEGAL_BASE && !!legalBase)
      return dataMap.dataProcessing.some(dp => dp.legalBase === legalBase);
    if (filter === FILTER.POSITION && !!position)
      return dataMap.dataProcessing.some(dp => dp.position === position);
    return true;
  });

  if (isPageView) {
    return (
      <>
        <HeaderCard>
          <ProcessLabel>{t('dashboard:ropa.show.processReportLabel')}</ProcessLabel>
          <ReportName>{ropaName}</ReportName>
          <ProcessMetaRow>
            <ScopeBadge $color="var(--vo-warning)" $bg="var(--vo-warning-10)">
              {t('dashboard:ropa.show.scopeLabel')}: {ropaScope}
            </ScopeBadge>
            <MetaDateText>{t('dashboard:ropa.table.generatedAt')} {formatDate(ropaDate)}</MetaDateText>
            <StatPill $color={BLUE} $bg="var(--vo-capri-50)">
              {totalData} {t('dashboard:ropa.show.statsMappedData')}
            </StatPill>
            <StatPill $color={GREEN} $bg="var(--vo-success-10)">
              {totalTreatments} {t('dashboard:ropa.show.statsTreatments')}
            </StatPill>
          </ProcessMetaRow>
          <ResponsibleRow>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <ResponsibleText>
              {t('dashboard:ropa.show.responsible')}: <strong>{process.user || '—'}</strong>
            </ResponsibleText>
            {effectiveArea && (
              <>
                <DotSep>·</DotSep>
                <AreaLbl>{t('dashboard:ropa.show.areaLabel')}: {effectiveArea}</AreaLbl>
              </>
            )}
          </ResponsibleRow>
        </HeaderCard>

        {process.descriptionDataFlow && (
          <DescCard>
            <DescCardLabel>{t('mapping:ropa.process.dataFlowDescription')}</DescCardLabel>
            <DescCardText>{process.descriptionDataFlow}</DescCardText>
          </DescCard>
        )}

        <PageDataCard>
          <PageDataHeader>
            <PageDataTitle>{t('dashboard:ropa.show.mappedDataTitle')}</PageDataTitle>
            <div style={{ display: 'flex', gap: 8 }}>
              <StatPill $color={BLUE} $bg="var(--vo-capri-50)">
                {totalData} {t('dashboard:ropa.show.statsData')}
              </StatPill>
              <StatPill $color={GREEN} $bg="var(--vo-success-10)">
                {totalTreatments} {t('dashboard:ropa.show.statsTreatments')}
              </StatPill>
            </div>
          </PageDataHeader>

          {(!filteredData || filteredData.length === 0) ? (
            <EmptyTxt>{t('mapping:ropa.process.noData')}</EmptyTxt>
          ) : (
            filteredData.map((dataMap, dmIdx) => {
              const filteredTreatments = (dataMap.dataProcessing ?? []).filter(dp => {
                if (filter === FILTER.PROCESSING && !!processing) return dp.processing === processing;
                if (filter === FILTER.LEGAL_BASE && !!legalBase) return dp.legalBase === legalBase;
                if (filter === FILTER.POSITION && !!position) return dp.position === position;
                return true;
              });

              return (
                <PageDataBlock key={`${dataMap.name}-${dmIdx}`}>
                  <DataHeader>
                    <div>
                      <DataName>{dataMap.name}</DataName>
                      <DataCategoryBadge>
                        {categoryByKey.get(dataMap.category) ?? dataMap.category}
                      </DataCategoryBadge>
                    </div>
                    <DataTreatmentBadge>
                      {filteredTreatments.length} {t('dashboard:ropa.show.statsTreatments')}
                    </DataTreatmentBadge>
                  </DataHeader>

                  <InfoGrid>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.origin')}</CellLabel>
                      <CellValue>{dataMap.dataSource || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.holderProfile')}</CellLabel>
                      <CellValue>{dataMap.holderProfile || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.retention')}</CellLabel>
                      <CellValue>{dataMap.retentionPeriod || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.storage')}</CellLabel>
                      <CellValue>{dataMap.storage || '—'}</CellValue>
                    </GridCell>
                  </InfoGrid>

                  {filteredTreatments.map((dp, dpIdx) => (
                    <TreatmentBlock key={`${dp.processing}-${dpIdx}`} dp={dp} t={t} legalBaseByKey={legalBaseByKey} />
                  ))}
                </PageDataBlock>
              );
            })
          )}
        </PageDataCard>
      </>
    );
  }

  return (
    <ProcessWrap>
      <ProcessHeader onClick={() => setOpen(o => !o)}>
        <ProcessLeft>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polygon points="12 2 2 7 12 12 22 7 12 2"/>
            <polyline points="2 17 12 22 22 17"/>
            <polyline points="2 12 12 17 22 12"/>
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NameRow>
              <ProcessName>{process.name}</ProcessName>
              <Badge $color={BLUE} $bg="color-mix(in srgb, var(--vo-info) 12%, transparent)">
                {totalData} {t('mapping:ropa.process.data')}
              </Badge>
              <Badge $color={GREEN} $bg="color-mix(in srgb, var(--vo-success) 12%, transparent)">
                {totalTreatments} {t('dashboard:ropa.show.statsTreatments')}
              </Badge>
            </NameRow>
            <ResponsibleTxt>
              {t('dashboard:ropa.show.responsible')}: {process.user || '—'}
            </ResponsibleTxt>
          </div>
        </ProcessLeft>
        <Chevron
          $open={open}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke={BLUE} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"/>
        </Chevron>
      </ProcessHeader>

      {open && (
        <>
          {process.descriptionDataFlow && (
            <DescSection>
              <SectionLabel>{t('mapping:ropa.process.dataFlowDescription')}</SectionLabel>
              <DescText>{process.descriptionDataFlow}</DescText>
            </DescSection>
          )}

          {(!filteredData || filteredData.length === 0) ? (
            <EmptyTxt>{t('mapping:ropa.process.noData')}</EmptyTxt>
          ) : (
            filteredData.map((dataMap, dmIdx) => {
              const filteredTreatments = (dataMap.dataProcessing ?? []).filter(dp => {
                if (filter === FILTER.PROCESSING && !!processing) return dp.processing === processing;
                if (filter === FILTER.LEGAL_BASE && !!legalBase) return dp.legalBase === legalBase;
                if (filter === FILTER.POSITION && !!position) return dp.position === position;
                return true;
              });

              return (
                <DataBlock key={`${dataMap.name}-${dmIdx}`}>
                  <DataHeader>
                    <div>
                      <DataName>{dataMap.name}</DataName>
                      <DataCategoryBadge>
                        {categoryByKey.get(dataMap.category) ?? dataMap.category}
                      </DataCategoryBadge>
                    </div>
                    <DataTreatmentBadge>
                      {filteredTreatments.length} {t('dashboard:ropa.show.statsTreatments')}
                    </DataTreatmentBadge>
                  </DataHeader>

                  <InfoGrid>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.origin')}</CellLabel>
                      <CellValue>{dataMap.dataSource || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.holderProfile')}</CellLabel>
                      <CellValue>{dataMap.holderProfile || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.retention')}</CellLabel>
                      <CellValue>{dataMap.retentionPeriod || '—'}</CellValue>
                    </GridCell>
                    <GridCell>
                      <CellLabel>{t('mapping:ropa.process.storage')}</CellLabel>
                      <CellValue>{dataMap.storage || '—'}</CellValue>
                    </GridCell>
                  </InfoGrid>

                  {filteredTreatments.map((dp, dpIdx) => (
                    <TreatmentBlock key={`${dp.processing}-${dpIdx}`} dp={dp} t={t} legalBaseByKey={legalBaseByKey} />
                  ))}
                </DataBlock>
              );
            })
          )}
        </>
      )}
    </ProcessWrap>
  );
};
